import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { History, Calendar, BarChart3, FileText, ChevronRight, RefreshCw, Search, Filter, Eye, DollarSign, CheckCircle, XCircle, Clock } from 'lucide-react';
import Cookies from 'js-cookie';
import MainLayout from '../Layout/MainLayout';
import BudgetDetailsModal from './BudgetDetailsModal';

const BudgetHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, perPage: 20, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);

  // Modal state (same pattern as Bank Agent)
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [budgetData, setBudgetData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch workflow runs
  const fetchWorkflowRuns = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = Cookies.get('sankalvax_token');
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        per_page: pagination.perPage.toString()
      });

      if (statusFilter) {
        params.append('status', statusFilter);
      }

      const response = await fetch(`/api/budget/v2/workflows/executions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch budget workflow runs');
      }

      const data = await response.json();
      setWorkflowRuns(data.executions || []);
      setPagination(prev => ({ ...prev, total: data.total || 0 }));

    } catch (err) {
      console.error('Error fetching workflow runs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkflowRuns();
  }, [pagination.page, statusFilter]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { bg: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      running: { bg: 'bg-blue-100 text-blue-800 border-blue-200', icon: RefreshCw },
      awaiting_user: { bg: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
      failed: { bg: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
      cancelled: { bg: 'bg-gray-100 text-gray-800 border-gray-200', icon: XCircle }
    };

    const config = statusConfig[status] || statusConfig.cancelled;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${config.bg}`}>
        <Icon className="w-3 h-3" />
        {status?.replace(/_/g, ' ').toUpperCase() || 'UNKNOWN'}
      </span>
    );
  };

  const filteredRuns = workflowRuns.filter(run => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      run.run_id?.toLowerCase().includes(query) ||
      run.budget_name?.toLowerCase().includes(query) ||
      run.fiscal_year?.toString().includes(query)
    );
  });

  // Fetch budget details for a specific run (same pattern as Bank Agent)
  const fetchBudgetDetails = async (run) => {
    try {
      setLoadingDetails(true);
      setSelectedRun(run);

      const token = Cookies.get('sankalvax_token');

      const response = await fetch(`/api/budget/v2/workflows/results/${run.run_id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch budget data');
      }

      const data = await response.json();
      console.log('Budget workflow results:', data);

      if (data.success) {
        setBudgetData(data);
        setShowDetailsModal(true);
      } else {
        throw new Error(data.error || 'No budget data found for this run.');
      }

    } catch (err) {
      console.error('Error fetching budget details:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleViewDetails = (run) => {
    // Navigate to budget details or resume workflow
    if (run.status === 'awaiting_user' || run.status === 'running') {
      // Resume workflow
      navigate(`/budget-agent/workflow?runId=${run.run_id}`);
    } else {
      // For completed/cancelled budgets, fetch and show details modal
      fetchBudgetDetails(run);
    }
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedRun(null);
    setBudgetData(null);
  };

  return (
    <MainLayout title="Budget History">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <History className="w-7 h-7 text-indigo-600" />
              Budget Workflow History
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              View and manage past budget creation workflows
            </p>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => navigate('/budget-agent/workflow')}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              New Budget
            </button>
            <button
              onClick={fetchWorkflowRuns}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by budget name, fiscal year..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
              className="px-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="awaiting_user">Awaiting Input</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-indigo-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Workflow Runs List */}
        {!loading && filteredRuns.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Budget Workflows Found</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              Start a new budget creation workflow to see history here.
            </p>
            <button
              onClick={() => navigate('/budget-agent/workflow')}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Create Budget
            </button>
          </div>
        )}

        {!loading && filteredRuns.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Date / Run ID
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Budget Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Fiscal Year
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Current Step
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRuns.map((run) => (
                    <tr
                      key={run.run_id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                            <BarChart3 className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {formatDate(run.started_at)}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                              {run.run_id?.substring(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {run.budget_name || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {run.fiscal_year || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(run.status)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {run.current_step_name?.replace(/_/g, ' ') || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleViewDetails(run)}
                          disabled={loadingDetails && selectedRun?.run_id === run.run_id}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-lg hover:shadow-lg transition-all disabled:opacity-50"
                        >
                          {loadingDetails && selectedRun?.run_id === run.run_id ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            <>
                              <Eye className="w-4 h-4" />
                              {run.status === 'awaiting_user' ? 'Resume' : 'View'}
                              <ChevronRight className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total > pagination.perPage && (
              <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  Showing {((pagination.page - 1) * pagination.perPage) + 1} to{' '}
                  {Math.min(pagination.page * pagination.perPage, pagination.total)} of{' '}
                  {pagination.total} results
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page <= 1}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page * pagination.perPage >= pagination.total}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Budget Details Modal */}
        {showDetailsModal && budgetData && (
          <BudgetDetailsModal
            budgetData={budgetData}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default BudgetHistory;
